import { composeInitialState } from '@opengov/compose-app';
import { IRecordOf, RecordOf } from '@opengov/compose-app/immutable';
import { LocationDescriptorObject } from 'history';
import { List, Map } from 'immutable';
import * as qs from 'query-string';
import UrlPattern from 'url-pattern';

import { composeStateMachine } from '../module';

import { ILocation, IRoute, IRouteCache, IRouteMatchResult } from '../types';

export const Location = RecordOf<ILocation>({ hash: null, pathname: null, search: null, state: null });

export const RouteResult = RecordOf<IRouteMatchResult>({
  // Location attributes
  hash: null,
  pathname: null,
  search: null,
  queryParams: null,
  state: null,
  // Route match attributes
  params: null,
  result: null,
  route: null
});

export const RouteCacheEntry = RecordOf<IRouteCache>({ route: null, pattern: null, result: null });

export const initialState = composeInitialState({
  ready: false,
  location: RouteResult({
    hash: window.location.hash,
    pathname: window.location.pathname,
    search: window.location.search
  }),
  previous: null,
  routes: List<IRecordOf<IRouteCache>>()
});

export const initialStateWithRoutes = (routes: { [key: string]: IRoute }) => {
  const routeList = mapRoutes(Map(routes));

  return initialState.set('routes', routeList).set(
    'location',
    matchRoute(routeList, {
      hash: window.location.hash,
      pathname: window.location.pathname,
      search: window.location.search
    })
  );
};

export const matchRoute = (
  routeList: List<IRecordOf<IRouteCache>>,
  location: ILocation
): IRecordOf<IRouteMatchResult> => {
  // Discard query strings
  const pathname = location.pathname.split('?')[0];
  const matchedRoute = routeList.find(storedRoute => storedRoute.pattern.match(pathname));

  if (!matchedRoute) {
    return RouteResult({ ...location });
  }

  return RouteResult({
    hash: location.hash,
    pathname: location.pathname,
    search: location.search,
    queryParams: qs.parse(location.search),
    state: location.state,
    route: matchedRoute.route,
    params: matchedRoute.pattern.match(pathname),
    result: matchedRoute.result
  });
};

export const mapRoutes = (routes: Map<string, IRoute>, wildcard: boolean = false): List<IRecordOf<IRouteCache>> => {
  return List(
    routes
      .keySeq()
      .sort()
      .reverse()
      .map(route =>
        RouteCacheEntry({
          route,
          pattern: new UrlPattern(route, { segmentValueCharset: 'a-zA-Z0-9-_~ %=' }),
          result: routes.get(route)
        })
      )
  );
};

export const routerState = composeStateMachine('router', initialState, compose => ({
  // make these stateful effects instead of empty actions and middleware :D
  push: compose.action((state, payload: string | LocationDescriptorObject) => state),
  replace: compose.action((state, payload: string | LocationDescriptorObject) => state),
  go: compose.action((state, payload: number) => state),
  goBack: compose.payloadlessAction(state => state),
  goForward: compose.payloadlessAction(state => state),
  navigationRejected: compose.action((state, payload: any) => state),
  navigationAccepted: compose.action((state, payload: { name: string; params?: any; path?: string }) => state),

  readyForRoutes: compose.payloadlessAction(state => state.set('ready', true)),

  locationChanged: compose.action((state, payload: { location: ILocation }) => {
    const nextLocation = matchRoute(state.routes, payload.location);

    if (state.location.pathname === nextLocation.pathname && state.location.search === nextLocation.search) {
      return state;
    }

    return state.set('location', nextLocation).set('previous', state.location);
  })
}));

export const {
  actions: routerActions,
  actionCreators: routerActionCreators,
  selector: routerSelector,
  branchKey: routerBranchKey,
  reducer: routerReducer
} = routerState;
